import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, 
  Calendar, 
  Award, 
  History,
  BookOpen,
  MessageSquare
} from 'lucide-react';
import { companionApi } from '../services/api';
import { 
  PageLoading, 
  GlassCard,
  ContentBadge,
  SourceCitation
} from '../components/shared';
import RichTextRenderer from '../components/RichTextRenderer';

const CompanionDetail = () => {
  const { t } = useTranslation();
  const { companionId } = useParams();
  const [companion, setCompanion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanion = async () => {
      try {
        const response = await companionApi.getCompanion(companionId);
        setCompanion(response.data);
      } catch (error) {
        console.error('Error fetching companion:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanion();
  }, [companionId]);

  if (loading) return <PageLoading />;
  if (!companion) return <div className="text-center py-20 text-text-muted">{t('companions.not_found')}</div>;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div 
        className="relative py-20 px-4 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1770339030328-d8b7c76adff1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxlbWVyYWxkJTIwZ3JlZW4lMjBhYnN0cmFjdCUyMHRleHR1cmV8ZW58MHx8fHwxNzc1NDY5MDYxfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Link to="/companions" className="inline-flex items-center text-text-muted hover:text-primary mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('companions.back_to_list')}
          </Link>
          
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-secondary/30 flex items-center justify-center flex-shrink-0">
              <span className="font-arabic text-3xl text-primary">
                {companion.name_arabic.charAt(0)}
              </span>
            </div>
            <div>
              <h1 className="font-arabic text-3xl sm:text-4xl text-primary mb-2" dir="rtl">
                {companion.name_arabic}
              </h1>
              <h2 className="text-2xl sm:text-3xl font-serif text-text-primary mb-1">
                {companion.name_french || companion.name_english}
              </h2>
              {companion.nickname && (
                <p className="text-lg text-primary italic">{companion.nickname}</p>
              )}
              <p className="text-text-secondary mt-2">{companion.name_english}</p>
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-text-muted">
                {companion.birth_year && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {companion.birth_year} - {companion.death_year || '?'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Biography */}
            <GlassCard>
              <h3 className="font-serif text-xl text-text-primary mb-4">{t('companions.biography')}</h3>
              <RichTextRenderer
                value={companion.biography}
                testid="companion-biography"
                className="text-text-secondary leading-relaxed"
              />
            </GlassCard>

            {/* Related Events */}
            {companion.related_events && companion.related_events.length > 0 && (
              <GlassCard>
                <h3 className="font-serif text-xl text-text-primary mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-primary" />
                  {t('companions.historical_events')}
                </h3>
                <div className="space-y-4">
                  {companion.related_events.map((event, i) => (
                    <Link 
                      key={i}
                      to={`/events/${event.id}`}
                      className="block p-4 bg-surface-elevated rounded-lg hover:border-primary/30 border border-transparent transition-all"
                    >
                      <ContentBadge type="historical" className="mb-2" />
                      <p className="text-text-primary font-medium">{event.title_french || event.title_english}</p>
                      <p className="font-arabic text-sm text-primary" dir="rtl">{event.title_arabic}</p>
                      {event.date_hijri && (
                        <p className="text-xs text-text-muted mt-2">{event.date_hijri}</p>
                      )}
                    </Link>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* Sources */}
            {companion.sources && companion.sources.length > 0 && (
              <GlassCard>
                <h3 className="font-serif text-xl text-text-primary mb-4">{t('companions.sources')}</h3>
                <SourceCitation sources={companion.sources} />
              </GlassCard>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Relationship to Prophet */}
            <GlassCard>
              <h4 className="text-sm font-medium text-text-muted mb-2">{t('companions.relationship_to_prophet')}</h4>
              <p className="text-text-primary">{companion.relationship_to_prophet}</p>
            </GlassCard>

            {/* Notable Achievements */}
            {companion.notable_achievements && companion.notable_achievements.length > 0 && (
              <GlassCard>
                <h4 className="font-serif text-lg text-text-primary mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  {t('companions.notable_achievements')}
                </h4>
                <ul className="space-y-2">
                  {companion.notable_achievements.map((achievement, i) => (
                    <li key={i} className="flex items-start gap-2 text-text-secondary text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      {achievement}
                    </li>
                  ))}
                </ul>
              </GlassCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanionDetail;
